<template>
	<div class="task-list-empty-state">
		<img
			class="task-list-empty-state__image"
			:alt="$t('task_list_empty_state_header')"
			:src="imageUrl"
		/>
		<div class="task-list-empty-state__content-wrapper">
			<div class="task-list-empty-state__content">
				<SuiIcon
					name="icon-budget-24"
					class="task-list-empty-state__icon"
				/>
				<div class="task-list-empty-state__header">
					{{ $t('task_list_empty_state_header') }}
				</div>
				<div class="task-list-empty-state__text">
					{{ langvarForEmptyState }}
				</div>
				<div
					v-if="showActionButtons"
					class="task-list-empty-state__actions"
				>
					<SuiTextButton
						v-if="canModifyProject || canCreateTasks"
						class="task-list-empty-state__link"
						:aria-label="$t('add_task_set')"
						@click="onAddTaskSet"
					>
						<SuiIcon name="icon-taskbundle-16" />
						{{ $t('add_task_set') }}
					</SuiTextButton>

					<SuiTextButton
						v-if="canSeeQuoteToTasks"
						as="a"
						class="task-list-empty-state__link"
						:aria-label="
							$t('project_empty_task_list_quote_to_task')
						"
						:href="quoteToTasksUrl"
					>
						<SuiIcon name="icon-task-new-16" />
						{{ $t('project_empty_task_list_quote_to_task') }}
					</SuiTextButton>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { SuiTextButton, SuiIcon } from '@developers/scoro-ui';
import { Constants } from '@/constants';
import { useTaskListStore } from '@/features/Task/components/TaskList/store';
import { UNGROUPED_TASKS_ID } from '../constants';
import { type TaskListProjectFragment } from '@/generated/graphql';
import { computed } from 'vue';
import { PermissionAction } from '@/constants/permissions';
import { usePermissions, useTranslation } from '@/composables';
import { type ValueOf } from '@/types';
import { TASK_DONE_FILTER } from '@/features/Task/constants';

export type TaskListEmptyStateProps = {
	project?: TaskListProjectFragment;
	taskDoneFilter?: ValueOf<typeof TASK_DONE_FILTER>;
};

const props = defineProps<TaskListEmptyStateProps>();

const { t } = useTranslation();
const taskListStore = useTaskListStore();
const { can } = usePermissions();

const imageUrl =
	'https://scoroassets.scoro.com/gfx/list_empty_illustration.svg';

const langvarForEmptyState = computed(() => {
	if (props.taskDoneFilter === TASK_DONE_FILTER.Done) {
		return t('task_list_empty_state_text_done');
	}
	if (props.taskDoneFilter === TASK_DONE_FILTER.NotDone) {
		return t('task_list_empty_state_text_not_done');
	}
	return t('task_list_empty_state_text');
});

const showActionButtons = computed(
	() => props.taskDoneFilter !== TASK_DONE_FILTER.Done
);

const quoteToTasksUrl = computed(() => {
	return `/${Constants.Module.QUOTES.name}/${Constants.Feature.QUOTE_TO_TASKS}/${props.project?.createTasksFromQuoteId}`;
});

const canModifyProject = computed(
	() => props.project?.permissions?.canModify || false
);

const canCreateTasks = computed(() =>
	can(PermissionAction.CREATE, Constants.Type.Task)
);

const canSeeQuoteToTasks = computed(() => {
	return !!(
		props.project?.createTasksFromQuoteId &&
		(canCreateTasks.value || canModifyProject.value)
	);
});

const onAddTaskSet = () => {
	taskListStore.taskBundleOpen = UNGROUPED_TASKS_ID;
};
</script>

<style lang="scss" scoped>
.task-list-empty-state {
	--sui-icon-fill: #{$blue-50};

	position: relative;
	max-width: 65rem;
	margin: 0 auto 2rem;

	&__image {
		width: 100%;
		height: auto;
	}

	&__content-wrapper {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		text-align: center;
		align-items: center;
		justify-content: center;
	}

	&__content {
		max-width: 40rem;
	}

	&__icon {
		--sui-icon-fill: #{$neutral-40};

		margin: 0 auto 0.5rem;
	}

	&__header {
		font-size: 2.25rem;
		font-weight: $scoro-fw-semi-bold;
		line-height: 2.75rem;
		color: $neutral-90;
		margin-bottom: 2rem;
	}

	&__text {
		font-size: 1.625rem;
		line-height: 2.5rem;
		color: $neutral-80;
		margin-bottom: 2rem;
	}

	&__actions {
		display: flex;
		justify-content: center;
		gap: 1rem;
	}

	&__link {
		color: $blue-50;
		cursor: pointer;

		&:hover {
			--sui-color-link-hover: #{$red-50};

			color: $red-50;
		}
	}
}
</style>
